/**
 * AddMailAccountModal
 *
 * Step 1 — simple: label + email + password → auto-detects provider → tests.
 *   • If test passes → saves and closes.
 *   • If test fails  → expands to Step 2 (manual IMAP/SMTP config).
 *
 * UX for app-password requirement:
 *   • Proactive: when a known provider (Gmail/iCloud/Outlook…) is detected
 *     from the email domain, a step-by-step help block is shown under the
 *     password field BEFORE the user submits.
 *   • Reactive: when the connection fails with an app-password error, a
 *     "Comment faire ?" CTA appears; clicking it expands the same inline
 *     guide (or a generic fallback for unknown providers).
 */

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  useCreateMailAccount,
  useTestMailAccount,
} from "@workspace/api-client-react";
import {
  CheckCircle2,
  XCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
  Mail,
  Lock,
  User,
  Info,
  ExternalLink,
  HelpCircle,
} from "lucide-react";
import {
  detectProvider,
  type MailProvider,
} from "@/lib/mail-providers";

// ─── Fallback server guessing for unknown domains ──────────────────────────────

function guessServers(email: string) {
  const domain = email.split("@")[1]?.toLowerCase() ?? "";
  return {
    imapHost: domain ? `imap.${domain}` : "",
    imapPort: 993,
    imapSecure: true,
    smtpHost: domain ? `smtp.${domain}` : "",
    smtpPort: 465,
    smtpSecure: true,
  };
}

// ─── Provider help block ───────────────────────────────────────────────────────

function ProviderHelpBlock({
  provider,
  defaultExpanded = true,
}: {
  provider: MailProvider;
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  if (!provider.help) return null;
  const { help } = provider;
  return (
    <div
      className="rounded-xl border border-primary/30 bg-primary/5 text-sm"
      data-testid="provider-help"
    >
      <button
        type="button"
        className="w-full flex items-start gap-2 p-3 text-left"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        <Info className="w-4 h-4 text-primary shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-foreground text-sm leading-snug">
            {help.headline}
          </p>
          {!expanded && help.intro && (
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
              {help.intro}
            </p>
          )}
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
        ) : (
          <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
        )}
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-3 border-t border-primary/10 pt-3">
          {help.intro && (
            <p className="text-xs text-muted-foreground leading-relaxed">
              {help.intro}
            </p>
          )}
          <ol className="space-y-2 list-decimal list-inside text-xs leading-relaxed">
            {help.steps.map((s) => (
              <li key={s.title}>
                <span className="font-medium text-foreground">{s.title}.</span>{" "}
                <span className="text-muted-foreground">{s.description}</span>
              </li>
            ))}
          </ol>
          {help.docUrl && (
            <a
              href={help.docUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline font-medium"
            >
              {help.docLabel ?? "Documentation officielle"}
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Generic app-password guide (shown when provider is unknown) ───────────────

function GenericAppPasswordGuide() {
  return (
    <div
      className="rounded-xl border border-primary/30 bg-primary/5 p-4 text-sm space-y-3"
      data-testid="generic-app-password-help"
    >
      <div className="flex items-start gap-2">
        <Info className="w-4 h-4 text-primary shrink-0 mt-0.5" />
        <div className="space-y-1">
          <p className="font-semibold text-foreground">
            Votre fournisseur exige un mot de passe d'application
          </p>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Certains fournisseurs (Gmail, iCloud, Outlook…) refusent votre mot
            de passe habituel et exigent un mot de passe spécifique à
            l'application lorsque la double authentification est activée.
          </p>
        </div>
      </div>
      <ol className="space-y-2 list-decimal list-inside text-xs leading-relaxed">
        <li>
          <span className="font-medium text-foreground">Connectez-vous à votre fournisseur.</span>{" "}
          <span className="text-muted-foreground">
            Rendez-vous sur le site officiel de votre messagerie (Gmail, iCloud, Outlook…).
          </span>
        </li>
        <li>
          <span className="font-medium text-foreground">Accédez aux paramètres de sécurité.</span>{" "}
          <span className="text-muted-foreground">
            Cherchez « Mots de passe d'application », « App password » ou
            « Mot de passe spécifique à une app » dans les options de sécurité
            ou de connexion.
          </span>
        </li>
        <li>
          <span className="font-medium text-foreground">Générez et collez.</span>{" "}
          <span className="text-muted-foreground">
            Créez un nouveau mot de passe (nommez-le « OneNext »), copiez-le et
            collez-le dans le champ mot de passe ci-dessus.
          </span>
        </li>
      </ol>
      <div className="flex flex-wrap gap-2 text-xs pt-1">
        <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1 text-primary hover:underline">
          Gmail <ExternalLink className="w-3 h-3" />
        </a>
        <span className="text-muted-foreground">·</span>
        <a href="https://appleid.apple.com/account/manage" target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1 text-primary hover:underline">
          iCloud <ExternalLink className="w-3 h-3" />
        </a>
        <span className="text-muted-foreground">·</span>
        <a href="https://account.microsoft.com/security" target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1 text-primary hover:underline">
          Outlook <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
}

// ─── Types ─────────────────────────────────────────────────────────────────────

interface ManualConfig {
  imapHost: string;
  imapPort: number;
  imapSecure: boolean;
  imapUsername: string;
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  smtpUsername: string;
}

type TestResult = {
  imap: { ok: boolean; error?: string };
  smtp: { ok: boolean; error?: string };
};

// ─── Component ─────────────────────────────────────────────────────────────────

export default function AddMailAccountModal({
  open,
  onOpenChange,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSaved: () => void;
}) {
  const { toast } = useToast();

  const [label, setLabel] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [showManual, setShowManual] = useState(false);
  const [manual, setManual] = useState<ManualConfig>({
    imapHost: "",
    imapPort: 993,
    imapSecure: true,
    imapUsername: "",
    smtpHost: "",
    smtpPort: 465,
    smtpSecure: true,
    smtpUsername: "",
  });

  // Provider detected proactively while the user types their email
  const [detectedProvider, setDetectedProvider] = useState<MailProvider | null>(null);

  const [testResult, setTestResult] = useState<TestResult | null>(null);

  // "Comment faire ?" accordion — only shown reactively after an app-password
  // error when there is no proactive guide already visible (i.e. the provider
  // was not auto-detected from the email domain).
  const [showAppPasswordGuide, setShowAppPasswordGuide] = useState(false);

  const testMutation = useTestMailAccount({ mutation: {} });
  const createMutation = useCreateMailAccount({
    mutation: {
      onSuccess: () => {
        toast({ title: "Compte mail connecté avec succès" });
        onSaved();
        handleClose();
      },
      onError: (err: Error) =>
        toast({
          title: "Erreur lors de l'ajout",
          description: err.message,
          variant: "destructive",
        }),
    },
  });

  function handleClose() {
    onOpenChange(false);
    setTimeout(() => {
      setLabel("");
      setEmail("");
      setPassword("");
      setShowManual(false);
      setManual({
        imapHost: "",
        imapPort: 993,
        imapSecure: true,
        imapUsername: "",
        smtpHost: "",
        smtpPort: 465,
        smtpSecure: true,
        smtpUsername: "",
      });
      setDetectedProvider(null);
      setTestResult(null);
      setShowAppPasswordGuide(false);
    }, 300);
  }

  function setManualField<K extends keyof ManualConfig>(k: K, v: ManualConfig[K]) {
    setManual((prev) => ({ ...prev, [k]: v }));
  }

  function handleEmailChange(value: string) {
    setEmail(value);
    setTestResult(null);
    setShowManual(false);
    setShowAppPasswordGuide(false);
    const provider = detectProvider(value.trim());
    setDetectedProvider(provider);
  }

  function buildPayload(cfg: ManualConfig) {
    const trimEmail = email.trim();
    return {
      label: label.trim() || trimEmail,
      emailAddress: trimEmail,
      fromName: null,
      signature: null,
      isDefault: true,
      imapHost: cfg.imapHost,
      imapPort: cfg.imapPort,
      imapSecure: cfg.imapSecure,
      imapUsername: cfg.imapUsername || trimEmail,
      imapPassword: password,
      smtpHost: cfg.smtpHost,
      smtpPort: cfg.smtpPort,
      smtpSecure: cfg.smtpSecure,
      smtpUsername: cfg.smtpUsername || trimEmail,
      smtpPassword: password,
      autoAnalyzeFromContains: [],
      autoSync: true,
    };
  }

  async function handleConnect(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast({ title: "Adresse e-mail et mot de passe requis", variant: "destructive" });
      return;
    }

    const provider = detectProvider(email.trim());
    const guess = guessServers(email.trim());

    const cfg: ManualConfig = {
      imapHost: provider?.imapHost ?? guess.imapHost,
      imapPort: provider?.imapPort ?? 993,
      imapSecure: provider?.imapSecure ?? true,
      imapUsername: email.trim(),
      smtpHost: provider?.smtpHost ?? guess.smtpHost,
      smtpPort: provider?.smtpPort ?? 465,
      smtpSecure: provider?.smtpSecure ?? true,
      smtpUsername: email.trim(),
    };
    setManual(cfg);

    const payload = buildPayload(cfg);
    try {
      const result = await testMutation.mutateAsync({ data: payload });
      const normalized: TestResult = {
        imap: { ok: result.imap.ok, error: result.imap.error ?? undefined },
        smtp: { ok: result.smtp.ok, error: result.smtp.error ?? undefined },
      };
      setTestResult(normalized);
      if (normalized.imap.ok && normalized.smtp.ok) {
        createMutation.mutate({ data: payload });
      } else {
        setShowManual(true);
      }
    } catch {
      setShowManual(true);
    }
  }

  async function handleManualSave(e: React.FormEvent) {
    e.preventDefault();
    const payload = buildPayload(manual);

    try {
      const result = await testMutation.mutateAsync({ data: payload });
      const normalized: TestResult = {
        imap: { ok: result.imap.ok, error: result.imap.error ?? undefined },
        smtp: { ok: result.smtp.ok, error: result.smtp.error ?? undefined },
      };
      setTestResult(normalized);
      if (!result.imap.ok || !result.smtp.ok) {
        toast({ title: "La connexion a échoué, vérifiez les paramètres", variant: "destructive" });
        return;
      }
    } catch {
      toast({ title: "Erreur lors du test de connexion", variant: "destructive" });
      return;
    }

    createMutation.mutate({ data: payload });
  }

  // Detect app-password errors from the server's already-translated French text
  const errorTexts = [testResult?.imap.error, testResult?.smtp.error]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  const isAppPasswordError =
    errorTexts.includes("mot de passe d'application") ||
    errorTexts.includes("app password") ||
    errorTexts.includes("application-specific");

  // The proactive guide is visible whenever a recognized provider is detected —
  // it serves as the primary help surface and does NOT require a test failure.
  const hasProactiveGuide = !!(detectedProvider?.help);

  // The reactive CTA ("Comment faire ?") is shown only when:
  //  1. A test failed with an app-password signal AND
  //  2. No proactive guide is already visible (avoids showing the guide twice)
  const showCommentFaireBtn = isAppPasswordError && !hasProactiveGuide && !showAppPasswordGuide;

  // The reactive guide (expanded by the CTA) works with or without a detected
  // provider: uses ProviderHelpBlock when available, GenericAppPasswordGuide otherwise.
  const showReactiveGuide = isAppPasswordError && !hasProactiveGuide && showAppPasswordGuide;

  const isBusy = testMutation.isPending || createMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md w-full max-h-[90vh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            Connecter une boîte mail
          </DialogTitle>
          <DialogDescription>
            Entrez votre adresse et mot de passe — la configuration se fait automatiquement.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 pr-1">
          {/* ── RGPD disclosure ── */}
          <div
            className="mt-2 rounded-lg border border-amber-300/60 bg-amber-50 dark:bg-amber-950/30 p-3 text-xs text-amber-900 dark:text-amber-100"
            data-testid="mail-openai-disclosure"
          >
            <p className="font-semibold mb-1">
              Traitement par intelligence artificielle (OpenAI)
            </p>
            <p className="leading-relaxed">
              En connectant cette boîte mail, vous acceptez que le contenu des
              e-mails synchronisés soit envoyé à OpenAI pour le tri et l'extraction de
              tâches. Vos identifiants IMAP/SMTP sont chiffrés (AES-256-GCM). Détails dans notre{" "}
              <a
                href={`${import.meta.env.BASE_URL}legal/confidentialite`}
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-medium"
              >
                politique de confidentialité
              </a>
              .
            </p>
          </div>

          {/* ── Form ── */}
          <form onSubmit={showManual ? handleManualSave : handleConnect} className="space-y-4 mt-3">
            <div className="space-y-1.5">
              <Label htmlFor="ma-label">
                <User className="w-3.5 h-3.5 inline mr-1" />
                Intitulé
              </Label>
              <Input
                id="ma-label"
                placeholder="Mon mail pro"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                disabled={isBusy}
                data-testid="input-label"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ma-email">
                <Mail className="w-3.5 h-3.5 inline mr-1" />
                Adresse e-mail
              </Label>
              <Input
                id="ma-email"
                type="email"
                placeholder="vous@domaine.fr"
                value={email}
                onChange={(e) => handleEmailChange(e.target.value)}
                disabled={isBusy}
                data-testid="input-email"
              />
              {detectedProvider && (
                <p className="text-xs text-primary font-medium">
                  Fournisseur détecté : {detectedProvider.label}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ma-password">
                <Lock className="w-3.5 h-3.5 inline mr-1" />
                Mot de passe
              </Label>
              <Input
                id="ma-password"
                type="password"
                placeholder="Mot de passe du compte mail"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isBusy}
                data-testid="input-imap-password"
              />
            </div>

            {/* ── Proactive guide — shown as soon as provider is detected ── */}
            {hasProactiveGuide && (
              <ProviderHelpBlock provider={detectedProvider!} defaultExpanded />
            )}

            {/* ── Test result banner ── */}
            {testResult && (
              <div className="rounded-lg border bg-secondary/30 p-3 text-sm space-y-2">
                <div className="flex items-start gap-2">
                  {testResult.imap.ok
                    ? <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                    : <XCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />}
                  <div>
                    <span className="font-medium">IMAP :</span>{" "}
                    {testResult.imap.ok
                      ? "OK"
                      : <span className="text-destructive">{testResult.imap.error}</span>}
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  {testResult.smtp.ok
                    ? <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                    : <XCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />}
                  <div>
                    <span className="font-medium">SMTP :</span>{" "}
                    {testResult.smtp.ok
                      ? "OK"
                      : <span className="text-destructive">{testResult.smtp.error}</span>}
                  </div>
                </div>

                {/* Context hint depending on error type */}
                {(!testResult.imap.ok || !testResult.smtp.ok) && (
                  <div className="pt-1">
                    {isAppPasswordError && hasProactiveGuide ? (
                      <p className="text-xs text-muted-foreground">
                        Suivez le guide ci-dessus pour générer votre mot de passe d'application.
                      </p>
                    ) : isAppPasswordError ? (
                      /* "Comment faire ?" CTA for unknown providers */
                      showCommentFaireBtn ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="text-xs gap-1.5"
                          onClick={() => setShowAppPasswordGuide(true)}
                          data-testid="btn-comment-faire"
                        >
                          <HelpCircle className="w-3.5 h-3.5" />
                          Comment faire ?
                        </Button>
                      ) : null
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        Corrigez les paramètres ci-dessous et réessayez.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ── Reactive guide — triggered by "Comment faire ?" ── */}
            {showReactiveGuide && (
              detectedProvider?.help
                ? <ProviderHelpBlock provider={detectedProvider} defaultExpanded />
                : <GenericAppPasswordGuide />
            )}

            {/* ── Step 2 : manual IMAP/SMTP config ── */}
            {showManual && (
              <div className="space-y-3">
                <p className="text-xs font-medium text-muted-foreground">
                  Configuration avancée (pré-remplie, modifiable)
                </p>

                <fieldset className="border border-border rounded-xl p-3 space-y-3">
                  <legend className="text-[11px] uppercase tracking-wider text-muted-foreground px-2">
                    IMAP (réception)
                  </legend>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">Serveur</Label>
                      <Input
                        value={manual.imapHost}
                        onChange={(e) => setManualField("imapHost", e.target.value)}
                        placeholder="imap.domaine.fr"
                        className="h-8 text-sm"
                        data-testid="input-imap-host"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Port</Label>
                      <Input
                        type="number"
                        value={manual.imapPort}
                        onChange={(e) => setManualField("imapPort", Number(e.target.value))}
                        className="h-8 text-sm"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Identifiant</Label>
                      <Input
                        value={manual.imapUsername}
                        onChange={(e) => setManualField("imapUsername", e.target.value)}
                        placeholder={email}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">SSL/TLS</Label>
                      <Select
                        value={manual.imapSecure ? "yes" : "no"}
                        onValueChange={(v) => setManualField("imapSecure", v === "yes")}
                      >
                        <SelectTrigger className="h-8 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">SSL/TLS</SelectItem>
                          <SelectItem value="no">STARTTLS</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </fieldset>

                <fieldset className="border border-border rounded-xl p-3 space-y-3">
                  <legend className="text-[11px] uppercase tracking-wider text-muted-foreground px-2">
                    SMTP (envoi)
                  </legend>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">Serveur</Label>
                      <Input
                        value={manual.smtpHost}
                        onChange={(e) => setManualField("smtpHost", e.target.value)}
                        placeholder="smtp.domaine.fr"
                        className="h-8 text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Port</Label>
                      <Input
                        type="number"
                        value={manual.smtpPort}
                        onChange={(e) => setManualField("smtpPort", Number(e.target.value))}
                        className="h-8 text-sm"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Identifiant</Label>
                      <Input
                        value={manual.smtpUsername}
                        onChange={(e) => setManualField("smtpUsername", e.target.value)}
                        placeholder={email}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">SSL/TLS</Label>
                      <Select
                        value={manual.smtpSecure ? "yes" : "no"}
                        onValueChange={(v) => setManualField("smtpSecure", v === "yes")}
                      >
                        <SelectTrigger className="h-8 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">SSL/TLS</SelectItem>
                          <SelectItem value="no">STARTTLS</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </fieldset>
              </div>
            )}

            {/* ── Actions ── */}
            <div className="flex gap-2 pt-1">
              {!showManual ? (
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={isBusy || !email.trim() || !password}
                  data-testid="button-save-account"
                >
                  {isBusy
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Connexion…</>
                    : "Connecter"}
                </Button>
              ) : (
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={isBusy}
                  data-testid="button-save-account"
                >
                  {isBusy
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Test en cours…</>
                    : "Tester et enregistrer"}
                </Button>
              )}
              <Button type="button" variant="outline" onClick={handleClose} disabled={isBusy}>
                Annuler
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
